import { GessanBaseAPI } from "@/api/gessan.base.api";
import { Form } from "@/models/gessan/form";
import { GessanApiEnvelope, TipoDocumentoGessan } from "@/models/gessan/tipoDocumento";
import { PaginationTableSaneamento } from "@/store/modules/gessan/states/table";
import { AxiosObservable } from "axios-observable/dist/axios-observable.interface";
import { Pagination } from "@/models/sit-pedido/pagination";
import { Lista } from "@/models/gessan/lista";
import {
  API_URL_SERVICE,
  VUE_APP_FINANCEIRO_API_URL,
  VUE_APP_GESSAN_URL,
} from "@/commons/config";
import { from } from "rxjs";
import axios from "axios";
import { Municipio } from "@/models/gessan/municipio";

export class SaneamentoApi extends GessanBaseAPI<Form> {
  constructor() {
    super();
    this.apiUrl = VUE_APP_GESSAN_URL + "/centralServicos";
  }

  public customAxios = axios.create();

  private getInformeRepasseBasePath(isGrupo?: boolean): string {
    const resource = isGrupo
      ? "informeRepasseRegulacaoGrupoMunicipio"
      : "informeRepasseRegulacao";

    return `${this.apiUrl}/${resource}`;
  }

  private normalizeIntegerParam(value?: string | number | null): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const normalizedValue = value.toString().replaceAll(".", "").replaceAll(",", ".");
    const parsedValue = normalizedValue ? Number(normalizedValue) : null;

    return parsedValue !== null && !Number.isNaN(parsedValue) ? parsedValue : null;
  }

  private normalizeDecimalParam(value?: string | number | null): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const normalizedValue = value.toString().replaceAll(".", "").replaceAll(",", ".");
    const parsedValue = normalizedValue ? Number(normalizedValue) : null;

    return parsedValue !== null && !Number.isNaN(parsedValue) ? parsedValue : null;
  }

  private normalizeListParams(filtro: PaginationTableSaneamento): PaginationTableSaneamento {
    return {
      ...filtro,
      qtdeUnAbastecimentoAgua: this.normalizeIntegerParam(
        filtro.qtdeUnAbastecimentoAgua
      ),
      qtdeUnEsgotamentoSanitario: this.normalizeIntegerParam(
        filtro.qtdeUnEsgotamentoSanitario
      ),
      faturamentoMensalLiquido: this.normalizeDecimalParam(
        filtro.faturamentoMensalLiquido
      ),
    };
  }

  public cadastro(formData: FormData, isGrupo?: boolean): AxiosObservable<any> {
    return this.axios.post(
      `${this.getInformeRepasseBasePath(isGrupo)}/`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  public getList(
    filtro: PaginationTableSaneamento,
    isGrupo?: boolean
  ): AxiosObservable<Pagination<Lista>> {
    return this.axios.get(`${this.getInformeRepasseBasePath(isGrupo)}/lista`, {
      params: this.normalizeListParams(filtro),
    });
  }

  public downloadDAEVencido(
    dae: string,
    dataVencimento: string,
    cnpj: string,
    isGrupo?: boolean
  ): AxiosObservable<Blob> {
    // @ts-ignore
    return from(
      this.axios.post(
        `${this.getInformeRepasseBasePath(isGrupo)}/emissaoDaeVencido`,
        { cnpj, dae, dataVencimento }
      )
    );
  }

  public downloadDAE(dae: string): AxiosObservable<Blob> {
    // @ts-ignore
    return from(
      this.customAxios.get(
        `${VUE_APP_FINANCEIRO_API_URL}/api/pdf/detran/dae/${dae}`,
        {
          responseType: "blob",
          headers: {
            Accept: "application/pdf",
          },
        }
      )
    );
  }

  public getMunicipioLogado(cnpj: string): AxiosObservable<Municipio> {
    return this.axios.post(`${this.apiUrl}/municipio/findByCnpj`, { cnpj });
  }

  public findByCnpjToken(cnpj: string, token: string): AxiosObservable<Municipio> {
    return this.axios.post(`${this.apiUrl}/municipio/findByCnpjToken`, { cnpj, token });
  }

  public getTiposDocumento(
    cnpj: string,
    isGrupo: boolean
  ): AxiosObservable<GessanApiEnvelope<TipoDocumentoGessan[]>> {
    return this.axios.get(`${this.apiUrl}/municipio/tipoDocumentoList`, {
      params: { cnpj, isGrupo },
    });
  }

  public getSituacoes(cnpj: string, isGrupo?: boolean): AxiosObservable<string[]> {
    return this.axios.get(
      `${this.getInformeRepasseBasePath(isGrupo)}/situacaoInformeListAll?cnpj=${cnpj}`
    );
  }

  public historicoCentral(form: {
    cnpj: string;
    nomeResponsavel: string;
    cpfResponsavel: string;
    aceite: boolean;
    competencia: string;
  }) {
    // @ts-ignore
    return from(
      this.customAxios.post(
        `${API_URL_SERVICE}/api/gessan/aceiteRepasse`,
        { ...form },
        {
          responseType: "blob",
          headers: {
            Accept: "application/json",
          },
        }
      )
    );
  }

  public downloadQuitacao(
    id: number,
    cnpj: string,
    isGrupo?: boolean
  ): AxiosObservable<any> {
    return this.axios.get(
      `${this.getInformeRepasseBasePath(
        isGrupo
      )}/quitacaoInforme?informeId=${id}&cnpj=${cnpj}`
    );
  }

  public listaNegadosCancelados(
    cnpj: string,
    isGrupo?: boolean
  ): AxiosObservable<any> {
    return this.axios.get(
      `${this.getInformeRepasseBasePath(
        isGrupo
      )}/listaCanceladosNegados?cnpj=${cnpj}`
    );
  }

  public getSituacoesDAE(cnpj: string, isGrupo?: boolean): AxiosObservable<string[]> {
    return this.axios.get(
      `${this.getInformeRepasseBasePath(isGrupo)}/situacaoDaeListAll?cnpj=${cnpj}`
    );
  }

  public podeCriarInforme(
    cnpj: string,
    isGrupo?: boolean
  ): AxiosObservable<boolean | GessanApiEnvelope<boolean>> {
    return this.axios.get(
      `${this.getInformeRepasseBasePath(isGrupo)}/podeCriarInforme`,
      {
        params: { cnpj },
      }
    );
  }
}
